import { useEffect, useRef } from 'react'
import { rfStore } from '../lib/rfstore'
import type { Band } from '../lib/client'
import { useI18n } from '../lib/i18n'
import { corDoModoCanvas } from '../lib/sinalCores'

/**
 * Fita de dial: a escala de frequência retroiluminada, arrastável com o polegar.
 *
 * É a peça que só existe neste produto. Ela é três coisas ao mesmo tempo:
 *
 *  - o CONTROLE de sintonia (arrasta e move na banda)
 *  - o MAPA de atividade (pontos onde tem gente)
 *  - o PROFESSOR de plano de bandas (âmbar aceso onde VOCÊ pode transmitir,
 *    apagado onde só escuta)
 *
 * Esse terceiro papel é o que resolve um problema real: antes, o operador
 * descobria que não podia falar ali só depois de apertar o PTT e ver o botão
 * apagado. Agora está pintado no controle que ele já está usando.
 *
 * Canvas e requestAnimationFrame, sem setState no caminho quente — mesma regra
 * do S-meter e do waterfall (docs/06 §1).
 */

export interface DialSegment {
  low_khz: number
  high_khz: number
  /** Se o operador pode transmitir aqui, com a classe dele. */
  allowed: boolean
  /** Só na Faixa do Cidadão: o número do canal. */
  channel?: string
}

export interface DialPresence {
  freq_khz: number
  transmitting: boolean
}

/** Uma repetidora do catálogo, no formato que a fita precisa para desenhar. */
export interface DialRepeater {
  callsign: string
  city: string
  output_mhz: number
  /** FM é analógico; DMR, C4FM e D-STAR são digitais — /api/repeaters já diz. */
  mode: string
  can_access: boolean
}

export function DialStrip({
  band,
  segments,
  presences,
  repeaters,
  aberta = false,
  onTune,
}: {
  band: Band | null
  segments: DialSegment[]
  presences: DialPresence[]
  repeaters: DialRepeater[]
  /** Faixa do Cidadão: a escala é numerada por CANAL, não por frequência. */
  aberta?: boolean
  onTune: (khz: number) => void
}) {
  const { t } = useI18n()
  const ref = useRef<HTMLCanvasElement>(null)
  // Refs e não state: o desenho roda a 60 fps e não pode depender de render.
  const dados = useRef({ band, segments, presences, repeaters, aberta })
  dados.current = { band, segments, presences, repeaters, aberta }

  const onTuneRef = useRef(onTune)
  onTuneRef.current = onTune

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Quantos kHz cabem na largura visível. Janela estreita: o operador ajusta
    // fino com o polegar, e a banda inteira é para os botões de banda.
    const JANELA = 60

    let raf = 0
    let arrastando = false
    let xInicial = 0
    let freqInicial = 0

    const draw = () => {
      const { band, segments, presences, repeaters, aberta } = dados.current
      const s = rfStore.snapshot()
      const centro = s.freqKHz

      const dpr = window.devicePixelRatio || 1
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr
        canvas.height = h * dpr
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const px = (khz: number) => ((khz - centro) / JANELA + 0.5) * w

      ctx.clearRect(0, 0, w, h)

      // Fundo: o recesso da escala, mais escuro que o painel em volta.
      ctx.fillStyle = '#080a0d'
      ctx.fillRect(0, 0, w, h)

      // Segmentos onde ESTE operador pode transmitir: acesos em âmbar. O resto
      // fica escuro — a ausência de luz é a informação.
      for (const seg of segments) {
        if (!seg.allowed) continue
        const x0 = Math.max(0, px(seg.low_khz))
        const x1 = Math.min(w, px(seg.high_khz))
        if (x1 <= 0 || x0 >= w) continue
        ctx.fillStyle = 'rgba(224, 145, 58, 0.14)'
        ctx.fillRect(x0, 0, x1 - x0, h)
        // Um fio no rodapé marca a borda do privilégio, que é onde o operador
        // precisa saber que parou de poder falar.
        ctx.fillStyle = 'rgba(224, 145, 58, 0.42)'
        ctx.fillRect(x0, h - 2, x1 - x0, 2)
      }

      // Fora da banda: escurece de vez. O operador vê a borda da faixa antes
      // de encostar nela.
      if (band) {
        const escurecer = (a: number, b: number) => {
          if (b <= a) return
          ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
          ctx.fillRect(a, 0, b - a, h)
        }
        escurecer(px(centro - JANELA), px(band.low_khz))
        escurecer(px(band.high_khz), px(centro + JANELA))
      }

      ctx.font = '9px ui-monospace, monospace'
      ctx.textAlign = 'center'

      if (aberta) {
        // Na PX a escala é numerada por CANAL. Rádio PX de verdade nem tem VFO
        // contínuo — tem seletor, e o operador diz "canal 11", nunca "27.085".
        for (const seg of segments) {
          if (!seg.channel) continue
          const meioSeg = (seg.low_khz + seg.high_khz) / 2
          const x = px(meioSeg)
          if (x < -20 || x > w + 20) continue
          ctx.fillStyle = seg.allowed
            ? 'rgba(255,255,255,0.30)'
            : 'rgba(255,255,255,0.12)'
          ctx.fillRect(x, 0, 1, 10)
          ctx.fillStyle = seg.allowed ? 'rgba(163,173,185,0.9)' : 'rgba(111,121,134,0.9)'
          ctx.fillText(seg.channel, x, 22)
        }
      } else {
        // Marcas da escala: a cada 5 kHz, mais alta a cada 25.
        const passo = 5
        const inicio = Math.ceil((centro - JANELA / 2) / passo) * passo
        for (let f = inicio; f <= centro + JANELA / 2; f += passo) {
          const x = px(f)
          const grande = f % 25 === 0
          ctx.fillStyle = grande ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.12)'
          ctx.fillRect(x, 0, 1, grande ? 10 : 5)
          if (grande) {
            ctx.fillStyle = 'rgba(163,173,185,0.85)'
            ctx.fillText((f / 1000).toFixed(3), x, 22)
          }
        }
      }

      // Repetidoras da faixa: losango âmbar para FM (analógico), quadrado ciano
      // para digital (DMR/C4FM/D-STAR) — a mesma distinção que /api/repeaters já
      // traz nos dados. Esmaece quando a estação não consegue abrir daqui, mas
      // continua visível: ver o que existe por perto também ensina.
      for (const rp of repeaters) {
        const x = px(rp.output_mhz * 1000)
        if (x < -8 || x > w + 8) continue
        const y = 30
        ctx.globalAlpha = rp.can_access ? 1 : 0.35
        ctx.fillStyle = corDoModoCanvas(rp.mode)
        if (rp.mode === 'FM') {
          ctx.beginPath()
          ctx.moveTo(x, y - 4)
          ctx.lineTo(x + 4, y)
          ctx.lineTo(x, y + 4)
          ctx.lineTo(x - 4, y)
          ctx.closePath()
          ctx.fill()
        } else {
          ctx.fillRect(x - 3, y - 3, 6, 6)
        }
        ctx.globalAlpha = 1
      }

      // Quem está no ar. Transmitindo agora acende vermelho e maior — é o que o
      // olho tem que achar primeiro.
      for (const p of presences) {
        const x = px(p.freq_khz)
        if (x < -6 || x > w + 6) continue
        const y = h - 12
        ctx.beginPath()
        ctx.arc(x, y, p.transmitting ? 4.5 : 3, 0, Math.PI * 2)
        ctx.fillStyle = p.transmitting ? '#ef5f5f' : '#5fd39a'
        ctx.fill()
        if (p.transmitting) {
          ctx.beginPath()
          ctx.arc(x, y, 8, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(239,95,95,0.22)'
          ctx.fill()
        }
      }

      // Agulha central: onde você está. Fica no meio sempre; a escala é que anda.
      const meio = w / 2
      ctx.fillStyle = '#e0913a'
      ctx.fillRect(meio - 1, 0, 2, h)
      ctx.beginPath()
      ctx.moveTo(meio - 5, 0)
      ctx.lineTo(meio + 5, 0)
      ctx.lineTo(meio, 7)
      ctx.closePath()
      ctx.fill()

      raf = requestAnimationFrame(draw)
    }

    const kHzPorPixel = () => JANELA / (canvas.clientWidth || 1)

    let moveu = false

    const down = (e: PointerEvent) => {
      arrastando = true
      moveu = false
      xInicial = e.clientX
      freqInicial = rfStore.snapshot().freqKHz
      canvas.setPointerCapture(e.pointerId)
    }
    const move = (e: PointerEvent) => {
      if (!arrastando) return
      if (Math.abs(e.clientX - xInicial) > 4) moveu = true
      // Arrastar para a ESQUERDA sobe a frequência: a escala se move sob a
      // agulha, como num dial de verdade.
      const delta = (xInicial - e.clientX) * kHzPorPixel()
      const alvo = Math.round((freqInicial + delta) / 0.5) * 0.5
      onTuneRef.current(alvo)
    }
    const up = (e: PointerEvent) => {
      if (!arrastando) return
      arrastando = false

      // Toque sem arrastar num marcador de repetidora: sintoniza direto na
      // saída dela. Mesma conta de posição do draw(), sem esperar o próximo
      // quadro — o toque tem que responder na hora.
      if (!moveu) {
        const centro = rfStore.snapshot().freqKHz
        const w = canvas.clientWidth || 1
        const rect = canvas.getBoundingClientRect()
        const xToque = e.clientX - rect.left
        const px = (khz: number) => ((khz - centro) / JANELA + 0.5) * w
        for (const rp of dados.current.repeaters) {
          if (Math.abs(px(rp.output_mhz * 1000) - xToque) <= 10) {
            onTuneRef.current(rp.output_mhz * 1000)
            break
          }
        }
      }

      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        /* o ponteiro já pode ter sido liberado */
      }
    }

    canvas.addEventListener('pointerdown', down)
    canvas.addEventListener('pointermove', move)
    canvas.addEventListener('pointerup', up)
    canvas.addEventListener('pointercancel', up)

    raf = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(raf)
      canvas.removeEventListener('pointerdown', down)
      canvas.removeEventListener('pointermove', move)
      canvas.removeEventListener('pointerup', up)
      canvas.removeEventListener('pointercancel', up)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      className="dial"
      aria-label={t.dock.escalaFrequenciaAria}
    />
  )
}
